<template>
  <div class="summernote-wrapper">
    <div ref="summernote"></div>
    <Modal
      :modalVisible="modalVisible"
      @close="closeModal"
      width="400px"
    >
      <template #modal-content>
        <div class="text-left">
          <h3 class="text-lg font-bold mb-4">Edit Alt Text</h3>
          <div class="mb-4">
            <label class="block text-sm font-medium mb-1">Alt Text</label>
            <input
              v-model="currentAltText"
              type="text"
              class="w-full border rounded px-3 py-2 focus:ring focus:ring-blue-400 focus:border-blue-400"
              placeholder="Enter description for image"
              @keyup.enter="saveAltText"
            />
          </div>
          <div class="flex justify-end space-x-2">
            <button
              @click="closeModal"
              class="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              @click="saveAltText"
              class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-medium transition"
            >
              Save
            </button>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import $ from "jquery"
import baseURL from "@/service/api"
import { getAuthAndMultipartHeader } from "@/util"
import Modal from "@/components/Modal/Modal"

export default {
  name: "TextEditorComponents",
  components: {
    Modal
  },
  props: {
    value: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      modalVisible: false,
      currentAltText: "",
      currentImageTarget: null
    }
  },
  mounted() {
    this.initEditor()
  },
  beforeDestroy() {
    try {
      $(this.$refs.summernote).summernote("destroy")
    } catch (e) {
      console.error(e)
    }
  },
  watch: {
    value(val) {
      if (val !== $(this.$refs.summernote).summernote("code")) {
        $(this.$refs.summernote).summernote("code", val)
      }
    }
  },
  methods: {
    initEditor() {
      const self = this

      const ImageButton = function (context) {
        const ui = $.summernote.ui
        const button = ui.button({
          contents: '<i class="note-icon-picture"/>',
          tooltip: "Insert Image",
          click: function () {
            const input = document.createElement("input")
            input.type = "file"
            input.accept = "image/*"
            input.onchange = (e) => {
              const file = e.target.files[0]
              if (file) {
                 self.sendFile(file)
              }
            }
            input.click()
          }
        })
        return button.render()
      }

      const EditAltTextButton = function (context) {
        const ui = $.summernote.ui
        const button = ui.button({
          contents: '<i class="note-icon-pencil"/> Alt',
          tooltip: "Edit Alt Text",
          click: function () {
            let $target = $(context.modules.imagePopover.target)
            // Fallback for different Summernote versions or contexts
            if (!$target.length) {
                $target = $(context.layoutInfo.editable.data("target"))
            }

            if ($target && $target.is("img")) {
              self.currentImageTarget = $target
              self.currentAltText = $target.attr("alt") || ""
              self.modalVisible = true
            } else {
                // Should hopefully not happen if button is in image popover
                console.warn("No image target found for Alt Text edit")
            }
          }
        })
        return button.render()
      }

      $(this.$refs.summernote).summernote({
        placeholder: "Write content here...",
        tabsize: 2,
        minHeight: 400,
        maxHeight: 600,
        styleTags: ["p", "h1", "h2", "h3", "h4", "h5", "h6"],
        toolbar: [
           ["style", ["style"]],
           ["font", ["bold", "underline", "clear"]],
           ["color", ["color"]],
           ["para", ["ul", "ol", "paragraph"]],
           ["table", ["table"]],
           ["insert", ["link", "customImage"]],
           ["view", ["fullscreen", "codeview"]]
        ],
        popover: {
          image: [
            ["custom", ["editAltText"]],
            ["image", ["resizeFull", "resizeHalf", "resizeQuarter", "resizeNone"]],
            ["float", ["floatLeft", "floatRight", "floatNone"]],
            ["remove", ["removeMedia"]]
          ]
        },
        buttons: { customImage: ImageButton, editAltText: EditAltTextButton },
        callbacks: {
          onChange: function (contents) {
            self.$emit("input", contents)
          },
          onImageUpload: function (files) {
            for (let i = 0; i < files.length; i++) self.sendFile(files[i])
          }
        }
      })
      
      // Set initial content
      if (this.value) {
        $(this.$refs.summernote).summernote("code", this.value)
      }
    },

    async sendFile(file) {
      const data = new FormData()
      data.append("image", file)
      try {
        const response = await baseURL.post("/images/upload", data, {
          headers: getAuthAndMultipartHeader()
        })
        $(this.$refs.summernote).summernote("insertImage", response.data.data.file_url, function ($image) {
          $image.css("max-width", "100%")
        })
      } catch (err) {
        console.error("Failed to upload image:", err)
        alert("Failed to upload image")
      }
    },

    closeModal() {
      this.modalVisible = false
      this.currentImageTarget = null
      this.currentAltText = ""
    },

    saveAltText() {
      if (this.currentImageTarget) {
        this.currentImageTarget.attr("alt", this.currentAltText)
        // Manually trigger change to ensure v-model updates
        const content = $(this.$refs.summernote).summernote("code")
        this.$emit("input", content)
      }
      this.closeModal()
    }
  }
}
</script>

<style lang="scss">
.summernote-wrapper {
  /* Ensure modal appears on top of summernote elements */
  ::v-deep .modal-wrapper {
    z-index: 9999 !important;
  }
}

.note-editable {
  h1 {
    font-size: 2.5rem !important;
    font-weight: bold !important;
    margin-bottom: 0.5rem !important;
  }
  h2 {
    font-size: 2rem !important;
    font-weight: bold !important;
    margin-bottom: 0.5rem !important;
  }
  h3 {
    font-size: 1.75rem !important;
    font-weight: bold !important;
    margin-bottom: 0.5rem !important;
  }
  h4 {
    font-size: 1.5rem !important;
    font-weight: bold !important;
    margin-bottom: 0.5rem !important;
  }
  h5 {
    font-size: 1.25rem !important;
    font-weight: bold !important;
    margin-bottom: 0.5rem !important;
  }
  h6 {
    font-size: 1rem !important;
    font-weight: bold !important;
    margin-bottom: 0.5rem !important;
  }
  ul {
    list-style-type: disc !important;
    padding-left: 1.5rem !important;
  }
  ol {
    list-style-type: decimal !important;
    padding-left: 1.5rem !important;
  }
  p {
    margin-bottom: 1rem !important;
  }
}
</style>
